/**
 * DebugVisualizer
 *
 * デバッグ用のビジュアライゼーション機能を提供
 * - マーカー境界ボックスの表示
 * - 優先度スコアのオーバーレイ
 * - アクティブマーカーのハイライト
 */

import * as THREE from 'three';

export class DebugVisualizer {
  /**
   * @param {Object} options - 設定オプション
   * @param {boolean} options.showBounds - 境界ボックス表示（デフォルト: true）
   * @param {boolean} options.showPriorityScores - 優先度スコア表示（デフォルト: true）
   * @param {boolean} options.showMarkerInfo - マーカー情報表示（デフォルト: true）
   * @param {HTMLElement} options.container - オーバーレイコンテナ（デフォルト: document.body）
   */
  constructor(options = {}) {
    this.showBounds = options.showBounds !== undefined ? options.showBounds : true;
    this.showPriorityScores =
      options.showPriorityScores !== undefined ? options.showPriorityScores : true;
    this.showMarkerInfo = options.showMarkerInfo !== undefined ? options.showMarkerInfo : true;
    this.container = options.container || document.body;

    // マーカーごとの境界ボックスとオーバーレイ
    this.markerVisuals = new Map(); // markerId -> { boundingBox, overlay }

    // HTML オーバーレイコンテナ
    this.overlayContainer = null;

    // T040: Performance metrics overlay
    this.performanceOverlay = null;
    this.showPerformanceMetrics =
      options.showPerformanceMetrics !== undefined ? options.showPerformanceMetrics : true;

    // 状態
    this.isEnabled = false;
    this.scene = null;
    this.camera = null;

    console.log('DebugVisualizer created');
  }

  /**
   * デバッグビジュアライゼーションを有効化
   * @param {THREE.Scene} scene - Three.js シーン
   * @param {THREE.Camera} camera - Three.js カメラ
   */
  enable(scene, camera) {
    if (this.isEnabled) {
      console.warn('DebugVisualizer already enabled');
      return;
    }

    this.scene = scene;
    this.camera = camera;

    // HTML オーバーレイコンテナを作成
    if (this.showPriorityScores || this.showMarkerInfo) {
      this._createOverlayContainer();
    }

    // T040: Create performance metrics overlay
    if (this.showPerformanceMetrics) {
      this._createPerformanceOverlay();
    }

    this.isEnabled = true;
    console.log('🔍 DebugVisualizer enabled');
  }

  /**
   * デバッグビジュアライゼーションを無効化
   */
  disable() {
    if (!this.isEnabled) {
      return;
    }

    // すべてのビジュアルを削除
    this.markerVisuals.forEach((visual) => {
      this._removeBoundingBox(visual.boundingBox);
      this._removeOverlay(visual.overlay);
    });
    this.markerVisuals.clear();

    // オーバーレイコンテナを削除
    if (this.overlayContainer && this.overlayContainer.parentNode) {
      this.overlayContainer.parentNode.removeChild(this.overlayContainer);
      this.overlayContainer = null;
    }

    // T040: Remove performance overlay
    if (this.performanceOverlay && this.performanceOverlay.parentNode) {
      this.performanceOverlay.parentNode.removeChild(this.performanceOverlay);
      this.performanceOverlay = null;
    }

    this.isEnabled = false;
    console.log('🔍 DebugVisualizer disabled');
  }

  /**
   * マーカー状態を更新
   * @param {Array<Object>} markerStates - MarkerState オブジェクトの配列
   * @param {Map<string, number>} priorityScores - 優先度スコアマップ
   * @param {string|null} activeMarkerId - アクティブなマーカーID
   */
  update(markerStates, priorityScores = new Map(), activeMarkerId = null) {
    if (!this.isEnabled || !this.scene || !this.camera) {
      return;
    }

    markerStates.forEach((state) => {
      const isActive = state.markerId === activeMarkerId;
      const isTracking = state.trackingStatus === 'tracking';
      const priorityScore = priorityScores.get(state.markerId);

      // ビジュアルが存在しない場合は作成
      if (!this.markerVisuals.has(state.markerId)) {
        this._createMarkerVisuals(state);
      }

      const visual = this.markerVisuals.get(state.markerId);

      // 境界ボックスの更新
      if (this.showBounds && visual.boundingBox) {
        this._updateBoundingBox(visual.boundingBox, state, isActive, isTracking);
      }

      // オーバーレイの更新
      if ((this.showPriorityScores || this.showMarkerInfo) && visual.overlay) {
        this._updateOverlay(visual.overlay, state, priorityScore, isActive, isTracking);
      }
    });
  }

  /**
   * オーバーレイコンテナを作成
   * @private
   */
  _createOverlayContainer() {
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.id = 'debug-overlay-container';
    this.overlayContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
    `;
    this.container.appendChild(this.overlayContainer);
  }

  /**
   * マーカーのビジュアル要素を作成
   * @param {Object} markerState - MarkerState オブジェクト
   * @private
   */
  _createMarkerVisuals(markerState) {
    const visual = {
      boundingBox: null,
      overlay: null,
    };

    // 境界ボックスを作成
    if (this.showBounds && markerState.anchor) {
      visual.boundingBox = this._createBoundingBox(markerState);
    }

    // オーバーレイを作成
    if ((this.showPriorityScores || this.showMarkerInfo) && this.overlayContainer) {
      visual.overlay = this._createOverlay(markerState);
    }

    this.markerVisuals.set(markerState.markerId, visual);
  }

  /**
   * 境界ボックスを作成
   * @param {Object} markerState - MarkerState オブジェクト
   * @returns {THREE.LineSegments}
   * @private
   */
  _createBoundingBox(markerState) {
    const bounds = markerState.markerDefinition.bounds;
    const aspectRatio = markerState.markerDefinition.aspectRatio || 0.707;

    // 境界ボックスのジオメトリ
    // bounds は正規化座標（0-1）なので、マーカーサイズに合わせてスケール
    const width = bounds.width;
    const height = bounds.height / aspectRatio;
    const x = bounds.x - 0.5; // 中心を原点に
    const y = (bounds.y - 0.5) / aspectRatio;

    const geometry = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      x,
      y,
      0,
      x + width,
      y,
      0,
      x + width,
      y + height,
      0,
      x,
      y + height,
      0,
      x,
      y,
      0,
    ]);
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    const material = new THREE.LineBasicMaterial({
      color: 0xff0000, // 赤色
      linewidth: 2,
      depthTest: false,
      depthWrite: false,
    });

    const line = new THREE.Line(geometry, material);
    line.renderOrder = 999; // 最前面に描画

    // マーカーのアンカーグループに追加
    markerState.anchor.group.add(line);

    return line;
  }

  /**
   * 境界ボックスを更新
   * @param {THREE.LineSegments} boundingBox - 境界ボックスオブジェクト
   * @param {Object} markerState - MarkerState オブジェクト
   * @param {boolean} isActive - アクティブかどうか
   * @param {boolean} isTracking - トラッキング中かどうか
   * @private
   */
  _updateBoundingBox(boundingBox, markerState, isActive, isTracking) {
    if (!boundingBox || !boundingBox.material) return;

    // 色の更新
    if (isActive) {
      boundingBox.material.color.setHex(0x00ff00); // 緑色（アクティブ）
      boundingBox.material.linewidth = 3;
    } else if (isTracking) {
      boundingBox.material.color.setHex(0xffff00); // 黄色（トラッキング中）
      boundingBox.material.linewidth = 2;
    } else {
      boundingBox.material.color.setHex(0xff0000); // 赤色（非アクティブ）
      boundingBox.material.linewidth = 1;
    }

    // 表示/非表示
    boundingBox.visible = isTracking;
  }

  /**
   * 境界ボックスを削除
   * @param {THREE.LineSegments} boundingBox - 境界ボックスオブジェクト
   * @private
   */
  _removeBoundingBox(boundingBox) {
    if (!boundingBox) return;

    if (boundingBox.parent) {
      boundingBox.parent.remove(boundingBox);
    }

    if (boundingBox.geometry) {
      boundingBox.geometry.dispose();
    }

    if (boundingBox.material) {
      boundingBox.material.dispose();
    }
  }

  /**
   * オーバーレイ要素を作成
   * @param {Object} markerState - MarkerState オブジェクト
   * @returns {HTMLElement}
   * @private
   */
  _createOverlay(_markerState) {
    const overlay = document.createElement('div');
    overlay.className = 'debug-marker-overlay';
    overlay.style.cssText = `
      position: absolute;
      padding: 4px 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-family: monospace;
      font-size: 12px;
      border-radius: 4px;
      white-space: nowrap;
      pointer-events: none;
      display: none;
    `;

    this.overlayContainer.appendChild(overlay);
    return overlay;
  }

  /**
   * オーバーレイを更新
   * @param {HTMLElement} overlay - オーバーレイ要素
   * @param {Object} markerState - MarkerState オブジェクト
   * @param {number|undefined} priorityScore - 優先度スコア
   * @param {boolean} isActive - アクティブかどうか
   * @param {boolean} isTracking - トラッキング中かどうか
   * @private
   */
  _updateOverlay(overlay, markerState, priorityScore, isActive, isTracking) {
    if (!overlay || !isTracking) {
      overlay.style.display = 'none';
      return;
    }

    // スクリーン座標を計算
    const screenPos = this._getScreenPosition(markerState.anchor.group);
    if (!screenPos) {
      overlay.style.display = 'none';
      return;
    }

    // オーバーレイの内容を構築
    let content = '';

    if (this.showMarkerInfo) {
      content += `<strong>${markerState.markerId}</strong><br>`;
      content += `Status: ${markerState.trackingStatus}<br>`;
    }

    if (this.showPriorityScores && priorityScore !== undefined) {
      content += `Priority: ${priorityScore.toFixed(3)}<br>`;
    }

    if (isActive) {
      content += `<span style="color: #00ff00;">★ ACTIVE</span>`;
    }

    overlay.innerHTML = content;

    // 位置を設定
    overlay.style.left = `${screenPos.x}px`;
    overlay.style.top = `${screenPos.y}px`;
    overlay.style.display = 'block';

    // 背景色を変更
    if (isActive) {
      overlay.style.background = 'rgba(0, 255, 0, 0.8)';
      overlay.style.color = 'black';
    } else {
      overlay.style.background = 'rgba(0, 0, 0, 0.7)';
      overlay.style.color = 'white';
    }
  }

  /**
   * オーバーレイを削除
   * @param {HTMLElement} overlay - オーバーレイ要素
   * @private
   */
  _removeOverlay(overlay) {
    if (!overlay) return;

    if (overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }

  /**
   * Three.js オブジェクトのスクリーン座標を取得
   * @param {THREE.Object3D} object - Three.js オブジェクト
   * @returns {Object|null} { x, y } またはnull
   * @private
   */
  _getScreenPosition(object) {
    if (!object || !this.camera) return null;

    const vector = new THREE.Vector3();
    object.getWorldPosition(vector);
    vector.project(this.camera);

    // NDC座標（-1 to 1）をピクセル座標に変換
    const canvas = this.camera.userData.canvas || document.querySelector('canvas');
    if (!canvas) return null;

    const widthHalf = canvas.width / 2;
    const heightHalf = canvas.height / 2;

    return {
      x: vector.x * widthHalf + widthHalf,
      y: -(vector.y * heightHalf) + heightHalf,
    };
  }

  /**
   * 設定を更新
   * @param {Object} options - 設定オプション
   */
  updateConfig(options = {}) {
    if (options.showBounds !== undefined) {
      this.showBounds = options.showBounds;
    }

    if (options.showPriorityScores !== undefined) {
      this.showPriorityScores = options.showPriorityScores;
    }

    if (options.showMarkerInfo !== undefined) {
      this.showMarkerInfo = options.showMarkerInfo;
    }

    console.log('🔍 DebugVisualizer config updated:', {
      showBounds: this.showBounds,
      showPriorityScores: this.showPriorityScores,
      showMarkerInfo: this.showMarkerInfo,
    });
  }

  /**
   * 統計情報を取得
   * @returns {Object}
   */
  getStats() {
    return {
      isEnabled: this.isEnabled,
      markerCount: this.markerVisuals.size,
      showBounds: this.showBounds,
      showPriorityScores: this.showPriorityScores,
      showMarkerInfo: this.showMarkerInfo,
    };
  }

  /**
   * T040: Create performance metrics overlay
   * @private
   */
  _createPerformanceOverlay() {
    this.performanceOverlay = document.createElement('div');
    this.performanceOverlay.id = 'debug-performance-overlay';
    this.performanceOverlay.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      padding: 12px;
      background: rgba(0, 0, 0, 0.8);
      color: #00ff00;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      border-radius: 6px;
      border: 1px solid rgba(0, 255, 0, 0.3);
      pointer-events: none;
      z-index: 10000;
      min-width: 200px;
      line-height: 1.4;
    `;
    this.container.appendChild(this.performanceOverlay);
    console.log('📊 [T040] Performance overlay created');
  }

  /**
   * T040: Update performance metrics overlay
   * @param {Object} metrics - Performance metrics from PerformanceMonitor
   */
  updatePerformanceMetrics(metrics) {
    if (!this.performanceOverlay || !this.showPerformanceMetrics) {
      return;
    }

    let content =
      '<div style="font-weight: bold; margin-bottom: 6px; color: #ffffff;">⚡ Performance</div>';

    // FPS
    if (metrics.fps) {
      const fpsColor =
        metrics.fps.current >= 30 ? '#00ff00' : metrics.fps.current >= 24 ? '#ffaa00' : '#ff0000';
      content += `<div>FPS: <span style="color: ${fpsColor};">${metrics.fps.current}</span> (avg: ${metrics.fps.average.toFixed(1)})</div>`;
    }

    // Frame time
    if (metrics.frameTime) {
      content += `<div>Frame: ${metrics.frameTime.toFixed(2)}ms</div>`;
    }

    // Memory (Chrome only)
    if (metrics.memory && metrics.memory.used > 0) {
      const memColor = metrics.memory.deltaPercentage > 5 ? '#ff0000' : '#00ff00';
      content += `<div>Memory: ${metrics.memory.used} MB`;
      if (metrics.memory.baseline > 0) {
        content += ` (<span style="color: ${memColor};">+${metrics.memory.delta} MB</span>)`;
      }
      content += `</div>`;
    }

    // Detection rate
    if (metrics.detection) {
      content += `<div>Detection: ${metrics.detection.rate.toFixed(1)}/s (${metrics.detection.successRate.toFixed(0)}%)</div>`;
    }

    // Markers
    if (metrics.markers) {
      content += `<div>Markers: ${metrics.markers.tracking}/${metrics.markers.total}</div>`;
    }

    this.performanceOverlay.innerHTML = content;
  }

  /**
   * クリーンアップ
   */
  cleanup() {
    this.disable();
    this.scene = null;
    this.camera = null;
    console.log('🧹 DebugVisualizer cleaned up');
  }
}
